import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input } from "components/form";
import { EditIconTwo, FilterIcon, SearchIconTwo, CheckboxCheckedIcon, MoreIconButton, ChevronRightIcon, AddIcon } from "assets";
import { useNavigate } from "react-router-dom";
import { Spinner } from "components/loader";
import { ConversationSession, ConversationSessionsResponse, ProgressBar, useClickOutside, useMobile } from "hooks";

interface SidebarItem {
  id: string;
  assessmentId?: string;
  title: string;
  date?: Date;
  type: 'conversation' | 'category';
  isOpen?: boolean;
  children?: SidebarItem[];
  conversationId?: number;
  flow?: number;
  progress_bar?: ProgressBar;
}

interface SidebarProps {
  selectedItemId?: string;
  className?: string;
  onNewConversation?: () => void;
  loadingAssessmentSessions: boolean;
  assessmentSessionError: any;
  assessmentSessions: ConversationSessionsResponse | undefined;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

interface FilterData {
  role: string;
}

const ChatSidebar: React.FC<SidebarProps> = ({
  selectedItemId,
  className = "",
  onNewConversation,
  loadingAssessmentSessions,
  assessmentSessionError,
  assessmentSessions,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
}) => {
  const { isMobile } = useMobile({ size: 1280 });
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set(['today', 'yesterday', 'this-week', 'previous-30-days', 'previous-3-months', 'older']));
  const [mobileDropDown, setMobileDropdown] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalRef, () => setMobileDropdown(false));
  useClickOutside(filterRef, () => setIsFilterModalOpen(false));

  // Infinite scroll: observe sentinel element at the bottom of the list
  // Uses the scroll container as root so the sentinel is only "intersecting"
  // relative to the visible scroll area, not the whole viewport.
  // Also guards against auto-fetching when content doesn't overflow
  // (e.g. all sections are collapsed), so pages only load when the user
  // actually scrolls through the list.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer || !fetchNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          scrollContainer.scrollHeight > scrollContainer.clientHeight
        ) {
          fetchNextPage();
        }
      },
      { root: scrollContainer, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const filterValidationSchema = yup.object().shape({
    role: yup.string().default(''),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetFilter,
  } = useForm<FilterData>({
    resolver: yupResolver(filterValidationSchema),
  });

  // Memoize sidebar items transformation
  const sidebarItems = React.useMemo(() => {
    const mapConversation = (conversation: ConversationSession): SidebarItem => ({
      id: conversation.id.toString(),
      assessmentId: conversation?.assessment?.assessment_id || (conversation?.assessment?.id ? String(conversation.assessment.id) : undefined),
      title: conversation.title || `Conversation ${conversation.id}`,
      type: 'conversation' as const,
      conversationId: conversation.id,
      flow: conversation.flow,
      progress_bar: conversation.assessment?.progress_bar,
    });

    const transformConversationsToSidebarItems = (conversations: ConversationSession[]) => {
      if (!Array.isArray(conversations)) {
        console.error('Expected conversations to be an array, but got:', conversations);
        return [];
      }

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const todayConversations: ConversationSession[] = [];
      const yesterdayConversations: ConversationSession[] = [];
      const olderConversations: ConversationSession[] = [];

      conversations.forEach(conversation => {
        const conversationDate = new Date(conversation.created_at);
        const isToday = conversationDate.toDateString() === today.toDateString();
        const isYesterday = conversationDate.toDateString() === yesterday.toDateString();

        if (isToday) {
          todayConversations.push(conversation);
        } else if (isYesterday) {
          yesterdayConversations.push(conversation);
        } else {
          olderConversations.push(conversation);
        }
      });

      const items: SidebarItem[] = [];

      if (todayConversations.length > 0) {
        items.push({
          id: 'today',
          title: 'Today',
          date: today,
          type: 'category',
          isOpen: openCategories.has('today'),
          children: todayConversations.map(mapConversation),
        });
      }

      if (yesterdayConversations.length > 0) {
        items.push({
          id: 'yesterday',
          title: 'Yesterday',
          date: yesterday,
          type: 'category',
          isOpen: openCategories.has('yesterday'),
          children: yesterdayConversations.map(mapConversation),
        });
      }

      if (olderConversations.length > 0) {
        const olderGroups = groupOlderConversations(olderConversations);
        items.push(...olderGroups);
      }

      return items;
    };

    const groupOlderConversations = (conversations: ConversationSession[]): SidebarItem[] => {
      // Use a predefined order so groups always appear in the correct sequence
      const orderedKeys = ['This Week', 'Previous 30 days', 'Previous 3 Months', 'Older'] as const;
      const groups: Record<string, ConversationSession[]> = {};
      const now = new Date();

      conversations.forEach(conversation => {
        const conversationDate = new Date(conversation.created_at);
        const daysDiff = Math.floor((now.getTime() - conversationDate.getTime()) / (1000 * 60 * 60 * 24));

        let groupKey: string;
        if (daysDiff <= 7) {
          groupKey = 'This Week';
        } else if (daysDiff <= 30) {
          groupKey = 'Previous 30 days';
        } else if (daysDiff <= 90) {
          groupKey = 'Previous 3 Months';
        } else {
          groupKey = 'Older';
        }

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(conversation);
      });

      // Return groups in the predefined order (nearest first)
      return orderedKeys
        .filter(key => groups[key] && groups[key].length > 0)
        .map(groupName => ({
          id: groupName.toLowerCase().replace(/\s+/g, '-'),
          title: groupName,
          type: 'category' as const,
          isOpen: openCategories.has(groupName.toLowerCase().replace(/\s+/g, '-')),
          children: groups[groupName].map(mapConversation),
        }));
    };

    if (searchTerm.trim() === '') {
      return transformConversationsToSidebarItems(assessmentSessions?.results || []);
    } else {
      const filteredConversations = assessmentSessions?.results?.filter(conversation =>
        (conversation.title || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      return transformConversationsToSidebarItems(filteredConversations || []);
    }
  }, [searchTerm, assessmentSessions, openCategories]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterSubmit = (data: FilterData) => {
    setSearchTerm(data.role);
    setIsFilterModalOpen(false);
  };

  const handleClearFilter = () => {
    setSearchTerm('');
    resetFilter({ role: '' });
    setIsFilterModalOpen(false);
  };

  const toggleCategory = (itemId: string) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleDropdown = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleItemClick = (item: SidebarItem) => {
    if (item.type === 'conversation' && item.conversationId) {
      const paramName = item.flow === 1 ? 'id2' : 'id';
      const paramId = item.flow === 1 ? item.conversationId : item.assessmentId;
      navigate(`/employer/assessment?${paramName}=${paramId}`);
    }
  };

  const formatCategoryDate = (date?: Date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getProgressStages = (progressBar: ProgressBar) => {
    const stages = [
      { key: 'role_title', label: 'Role Title', status: progressBar.role_title },
      { key: 'job_description', label: 'Job Description', status: progressBar.job_description },
      { key: 'criteria', label: 'Criteria', status: progressBar.criteria },
      { key: 'technical_competence', label: 'Technical Competence', status: progressBar.technical_competence },
      { key: 'time_line', label: 'Timeline', status: progressBar.time_line },
      { key: 'done', label: 'Done', status: progressBar.done },
    ];

    return stages.filter(stage => stage.status !== null);
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return '#6B7280';
    switch (status) {
      case 'completed':
        return '#10B981';
      case 'in_progress':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const getStatusIcon = (status: string | null) => {
    if (!status) return '○';
    switch (status) {
      case 'completed':
        return <CheckboxCheckedIcon style={{ color: "#069952" }} />;
      case 'in_progress':
        return <MoreIconButton />;
      default:
        return '○';
    }
  };

  const renderConversationItem = (child: SidebarItem, isMobileList = false) => {
    const isDropdownOpen = openDropdowns.has(child.id);
    const stages = child.progress_bar ? getProgressStages(child.progress_bar) : [];
    const isActive = selectedItemId != null && (
      selectedItemId === child.assessmentId ||
      selectedItemId === child.conversationId?.toString()
    );
    const isCvAnalysis = child.flow === 1;

    return (
      <div key={child.id} className="relative">
        <div
          className={`flex items-center justify-between cursor-pointer transition-colors duration-300 text-[#012169] ${
            isMobileList ? 'px-[15px] py-1' : 'px-[26px] py-[7px] max-lg:px-4 max-lg:pt-2 max-lg:gap-[5px]'
          } ${isActive ? 'bg-[#e6f0ff] hover:bg-[#daeaff]' : 'hover:bg-[#edf2f7]'}`}
          onClick={() => handleItemClick(child)}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`overflow-hidden text-ellipsis whitespace-nowrap ${isMobileList ? 'max-w-[170px]' : 'max-w-[170px] max-lg:max-w-[130px]'} ${
                isActive
                  ? 'text-sm font-bold'
                  : isDropdownOpen && !isMobileList
                    ? 'text-sm font-medium'
                    : 'text-xs'
              }`}
              title={child.title}
            >
              {child.title}
            </span>
            {isCvAnalysis && (
              <span className="inline-flex items-center justify-center px-1.5 pb-[3.5px] pt-[2.5px] text-xs leading-[1.1] font-medium text-white bg-[#175CD3] border border-[#B2DDFF] rounded-[5px]">
                CV
              </span>
            )}
          </div>
          {stages.length > 0 && (
            <button
              className={`bg-transparent border-none cursor-pointer flex items-center justify-center p-0 transition-colors duration-300 hover:bg-transparent`}
              onClick={(e) => toggleDropdown(child.id, e)}
            >
              <ChevronRightIcon
                className={`w-2 h-5 transition-transform duration-300 rotate-90 [&_path]:stroke-gray-500 [&_path]:[stroke-width:0.5px] ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
          )}
        </div>

        {isDropdownOpen && stages.length > 0 && (
          <div
            className={`flex flex-col ${
              isMobileList
                ? 'mx-0 mr-5 ml-[14px] gap-0 bg-transparent'
                : 'mx-5 bg-white rounded-md z-20 gap-2'
            }`}
          >
            {stages.map((stage) => (
              <div
                key={stage.key}
                className={`sm:ml-3 flex items-center text-xs gap-2 transition-colors duration-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] cursor-pointer hover:bg-gray-50 ${
                  isMobileList
                    ? 'flex-row-reverse justify-end px-2 py-1 shadow-none'
                    : 'px-[15px] py-2 max-lg:px-2 max-lg:justify-between'
                } ${
                  stage.status === 'completed'
                    ? `${isMobileList ? 'border-none' : 'border border-[#069952]'} rounded`
                    : `${isMobileList ? 'border-none' : 'border border-[#53B1FD]'} rounded`
                }`}
              >
                <span
                  className={`flex-1 text-gray-700 font-medium ${
                    isMobileList
                      ? 'max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap text-xs font-normal'
                      : 'max-lg:max-w-[70px] max-lg:overflow-hidden max-lg:text-ellipsis max-lg:whitespace-nowrap'
                  }`}
                >
                  {stage.label}
                </span>
                <span
                  className={`flex items-center justify-center text-xs font-bold ${
                    isMobileList ? 'w-3 h-3' : 'w-4 h-4'
                  }`}
                  style={{ color: getStatusColor(stage.status) }}
                >
                  {getStatusIcon(stage.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCategoryList = (items: SidebarItem[], isMobileList = false) => {
    return items.map((item) => {
      if (item.type === 'category') {
        const hasRelativeDate = item.id === 'today' || item.id === 'yesterday';
        const categoryLabel = hasRelativeDate && item.date
          ? `${item.title}, ${formatCategoryDate(item.date)}`
          : item.title;

        return (
          <div key={item.id}>
            <div className={isMobileList ? 'py-[5px] px-[10px]' : 'py-[6px] px-[26px]'}>
              <div
                className="flex items-center justify-between text-[10px] font-semibold text-[#566B9B] cursor-pointer hover:text-[#605bff]"
                onClick={() => toggleCategory(item.id)}
              >
                <span>{categoryLabel}</span>
              </div>
            </div>

            {item.isOpen && item.children && (
              <div className="mt-1">
                {item.children.map((child) => renderConversationItem(child, isMobileList))}
              </div>
            )}
          </div>
        );
      } else {
        return renderConversationItem(item, isMobileList);
      }
    });
  };

  return (
    <>
      {/* Desktop / tablet sidebar */}
      <div className={`h-full flex flex-col overflow-visible bg-[#F7FBFF] max-sm:!hidden ${className}`}>
        <div className="px-[26px] pt-4 mt-3 mr-auto max-lg:px-4 max-lg:mt-2">
          <button
            className="flex items-center justify-start w-fit px-3 py-2 bg-white border border-[#012169] rounded-md text-[13px] font-medium text-[#012169] cursor-pointer transition-all duration-300 gap-2 hover:bg-[#f0f5ff] hover:border-[#bbc8e0] max-lg:px-[10px] max-lg:py-[7px] max-lg:pb-[6px] max-lg:w-full max-lg:text-xs max-lg:gap-[6px] max-lg:justify-start"
            onClick={onNewConversation}
          >
            <EditIconTwo className="w-5 h-5" />
            {isMobile ? 'New conversation' : 'Start a new conversation'}
          </button>
        </div>

        <div className="relative px-[26px] flex items-center mt-5 max-lg:px-4 max-lg:gap-[10px] max-lg:mt-[10px] max-lg:flex-col">
          <div ref={filterRef} className="relative max-lg:w-full">

            {isFilterModalOpen && (
              <div
                className="absolute top-full left-0 mt-2 bg-white border border-[#dae0e6] rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-4 z-20 min-w-[250px] max-lg:min-w-0 max-lg:w-[calc(100vw-2rem)]"
              >
                <form onSubmit={handleSubmit(handleFilterSubmit)}>
                  <Input
                    type="text"
                    name="role"
                    placeholder="Filter by role"
                    label="Role"
                    register={register}
                    validatorMessage={errors.role?.message}
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      type="submit"
                      className="flex-1 px-3 py-[6px] bg-[#012169] text-white text-xs font-medium rounded-md border-none cursor-pointer hover:bg-[#001a4d] transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      onClick={handleClearFilter}
                      className="flex-1 px-3 py-[6px] bg-white text-[#344054] text-xs font-medium rounded-md border border-[#D0D5DD] cursor-pointer hover:bg-[#F2F4F7] transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          <div className="relative flex items-center gap-[5px] w-full px-[10px] py-[7px] border border-neutral-300 rounded-md bg-white max-lg:px-2 max-lg:py-[6px] max-lg:text-xs">
            <SearchIconTwo />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchInputChange}
              className="w-full border-none text-xs text-[#0a1e42] bg-white transition-all duration-300 focus:outline-none placeholder:text-gray-500 placeholder:text-xs"
            />
          </div>
        </div>

        {/* flex-1 + min-h-0 allows the list to fill remaining space and scroll properly */}
        <div ref={scrollContainerRef} className="h-[calc(100vh-200px)] overflow-y-auto py-2 pb-8 scrollbar-none">
          {loadingAssessmentSessions ? (
            <div className="grid place-content-center overflow-hidden mt-[30px]">
              <Spinner className="w-[30px] h-[30px] border-[5px] border-[#f3f3f3] border-t-[5px] border-t-[#605bff]" />
            </div>
          ) : assessmentSessionError ? (
            <div className="pl-6">
              <p className="text-red-500">Error loading conversations</p>
            </div>
          ) : sidebarItems.length === 0 ? (
            <div className="pl-6">
              <p>No conversations found</p>
            </div>
          ) : (
            <>
              {renderCategoryList(sidebarItems)}

              {/* Sentinel element for infinite scroll */}
              <div ref={sentinelRef} className="h-px" />

              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <Spinner className="w-5 h-5 border-[3px] border-[#f3f3f3] border-t-[3px] border-t-[#605bff]" />
                </div>
              )}

            </>
          )}
        </div>
      </div>

      {/* Mobile header (shown < 640px) */}
      <header className="hidden max-sm:flex items-center justify-center relative bg-[#F5FAFF] h-[60px]">
        <div>
          <button
            className="bg-[#E5F3FF] rounded border-none flex gap-2 px-[10px] py-[6px]"
            onClick={() => setMobileDropdown(!mobileDropDown)}
          >
            <p className="text-[#012169] font-medium">
              {sidebarItems
                .flatMap(item => item.children || [])
                .find(child => selectedItemId != null && (selectedItemId === child.assessmentId || selectedItemId === child.conversationId?.toString()))?.title || "New Conversation"}
            </p>
            <ChevronRightIcon
              className={`w-2 h-5 transition-transform duration-300 rotate-90 [&_path]:stroke-gray-500 [&_path]:[stroke-width:0.5px] ${
                mobileDropDown ? 'rotate-180' : ''
              }`}
            />
          </button>
          {mobileDropDown && (
            <div
              ref={modalRef}
              className="absolute top-[80%] left-1/2 -translate-x-1/2 border border-[#D1E9FF] rounded-xl shadow-[0px_4px_8px_0px_rgba(0,0,0,0.1)] bg-[#F5FAFF] z-[99] max-h-[200px] w-[280px] overflow-y-scroll scrollbar-none py-[5px]"
            >
              <div className="max-h-full scrollbar-none">
                {loadingAssessmentSessions ? (
                  <div className="grid place-content-center overflow-hidden my-[30px]">
                    <Spinner className="w-[30px] h-[30px] border-[5px] border-[#f3f3f3] border-t-[5px] border-t-[#605bff]" />
                  </div>
                ) : assessmentSessionError ? (
                  <div className="pl-6">
                    <p className="text-red-500">Error loading conversations</p>
                  </div>
                ) : sidebarItems.length === 0 ? (
                  <div className="pl-6">
                    <p>No conversations found</p>
                  </div>
                ) : (
                  renderCategoryList(sidebarItems, true)
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className="absolute top-1/2 right-[15px] -translate-y-1/2 cursor-pointer rounded-full bg-[#1849A9] w-[25px] h-[25px] grid place-content-center"
          onClick={onNewConversation}
        >
          <AddIcon className="text-white w-3 h-3" />
        </div>
      </header>
    </>
  );
};

export { ChatSidebar };